import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, RefreshCw } from "lucide-react";

export default function AdminPasswordResets() {
  const { t } = useLang();
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("pending");
  const [resetForm, setResetForm] = useState({ open: false, request: null, password: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/password-resets?status=${tab}`);
      setRequests(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openResetForm = (req) => setResetForm({ open: true, request: req, password: generatePassword() });

  const submitReset = async () => {
    const { request, password } = resetForm;
    if (!password || password.length < 6) return toast.error(t("fields_required"));
    try {
      await api.post(`/admin/users/${request.user_id}/reset-password`, {
        new_password: password,
        request_id: request.id,
      });
      toast.success(t("password_reset_done"));
      setResetForm({ open: false, request: null, password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tabs = [
    { key: "pending", label: t("filter_pending") },
    { key: "resolved", label: t("filter_resolved") },
    { key: "all", label: t("filter_all") },
  ];

  return (
    <div className="space-y-4" data-testid="admin-password-resets-page">
      <h1 className="text-2xl sm:text-3xl font-semibold">{t("password_reset_requests")}</h1>

      <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl w-fit">
        {tabs.map((tt) => (
          <button
            key={tt.key}
            data-testid={`pwr-tab-${tt.key}`}
            onClick={() => setTab(tt.key)}
            className={`px-3 py-2 text-sm rounded-lg ${tab === tt.key ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
          >
            {tt.label}
          </button>
        ))}
      </div>

      {requests.length === 0 && <div className="bg-white border border-[#E5E1D8] rounded-2xl p-8 text-center text-[#5C6053] text-sm">—</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {requests.map((r) => (
          <div key={r.id} data-testid={`pwr-card-${r.id}`} className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{r.name || "—"}</div>
                <div className="text-xs text-[#5C6053] mt-1" dir="ltr">{r.phone}</div>
                <div className="text-xs text-[#5C6053]">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${r.status === "pending" ? "bg-[#FFF3D6] text-[#8A6D1F]" : "bg-[#E6F1E7] text-[#2A5948]"}`}>
                {r.status === "pending" ? t("filter_pending") : t("filter_resolved")}
              </span>
            </div>
            {r.status === "pending" && (
              <div className="mt-4 flex justify-end">
                <button data-testid={`pwr-reset-${r.id}`} onClick={() => openResetForm(r)} className="px-3 py-1.5 text-xs rounded-lg brand-btn inline-flex items-center gap-1">
                  <KeyRound size={14} /> {t("set_new_password")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {resetForm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1C1E17]/50 backdrop-blur-sm" onClick={() => setResetForm({ open: false, request: null, password: "" })}>
          <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl border border-[#E5E1D8]" onClick={(e) => e.stopPropagation()} data-testid="pwr-reset-form">
            <h3 className="text-lg font-semibold">{t("set_new_password")}</h3>
            <p className="text-sm text-[#5C6053] mt-1">
              {resetForm.request?.name} — <span dir="ltr">{resetForm.request?.phone}</span>
            </p>
            <div className="mt-4 flex items-center gap-2">
              <input
                data-testid="pwr-new-password-input"
                value={resetForm.password}
                onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                className="flex-1 border border-[#E5E1D8] bg-[#F9F6F0] rounded-lg px-3 py-2 text-sm outline-none"
                dir="ltr"
              />
              <button
                data-testid="pwr-regen"
                onClick={() => setResetForm({ ...resetForm, password: generatePassword() })}
                className="p-2 border border-[#E5E1D8] rounded-lg"
                title={t("regenerate")}
              >
                <RefreshCw size={14} />
              </button>
            </div>
            <div className="mt-2 text-xs text-[#5C6053]">{t("share_password_with_user")}</div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setResetForm({ open: false, request: null, password: "" })} className="px-3 py-2 rounded-lg text-sm border border-[#E5E1D8]">{t("cancel")}</button>
              <button data-testid="pwr-confirm-btn" onClick={submitReset} className="brand-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1">
                <CheckCircle2 size={14} /> {t("apply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function generatePassword() {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}
